import type { Facility } from './types';

/** Every real name on the facility's `Facility_Staff` team, for search. */
function teamNames(x: Facility): string[] {
  if (x.team.state !== 'present') return [];
  const t = x.team.data;
  return [t.propertyManager, ...t.assistants, ...t.seniors]
    .map((m) => m.name)
    .filter((n): n is string => !!n && n !== 'None' && n !== 'Now Hiring');
}

/**
 * The filtered, sorted facility list.
 *
 * This lives on its own because TWO screens must agree on it exactly: the
 * portfolio table renders it, and the detail header's stepper walks it ("4 of
 * 37", ← →). If they compute the set differently the stepper skips or repeats
 * facilities, which reads as data loss.
 *
 * Filter state lives in the URL, so a filtered view is shareable and the
 * stepper survives a reload.
 */

export type StatusFilter = 'Active' | 'Vacancy' | 'Unmanned' | 'Inactive' | 'All';
export type SortKey = 'code' | 'name' | 'brand' | 'district' | 'region' | 'units' | 'pm' | 'status';

export interface FilterState {
  q: string;
  region: string;
  brand: string;
  /**
   * FULL state NAME as stored ("California"), never a postal code — the source
   * column holds names, and comparing a two-letter code against it silently
   * returns nothing. The options are built from the data actually present, so
   * the dirty rows survive: one active site carries a CITY in its State column
   * ("San Antonio"), and it appears as its own option rather than vanishing.
   */
  state: string;
  /** District MANAGER name — that is what the District column holds. */
  district: string;
  status: StatusFilter;
  sort: SortKey;
  dir: 1 | -1;
  view: 'table' | 'map';
}

export const DEFAULT_FILTERS: FilterState = {
  q: '',
  region: 'All',
  brand: 'All',
  state: 'All',
  district: 'All',
  // Managed is the default, and this matters. Inactive facilities are sites
  // Westport no longer manages; opening on "everything" inflates every
  // operational flag with work that is not the company's problem.
  status: 'Active',
  sort: 'name',
  dir: 1,
  view: 'table',
};

export function readFilters(params: URLSearchParams): FilterState {
  const status = params.get('status') as StatusFilter | null;
  const sort = params.get('sort') as SortKey | null;
  const view = params.get('view');
  return {
    q: params.get('q') ?? DEFAULT_FILTERS.q,
    region: params.get('region') ?? DEFAULT_FILTERS.region,
    brand: params.get('brand') ?? DEFAULT_FILTERS.brand,
    state: params.get('state') ?? DEFAULT_FILTERS.state,
    district: params.get('district') ?? DEFAULT_FILTERS.district,
    status:
      status && ['Active', 'Vacancy', 'Unmanned', 'Inactive', 'All'].includes(status)
        ? status
        : DEFAULT_FILTERS.status,
    sort:
      sort && ['code', 'name', 'brand', 'district', 'region', 'units', 'pm', 'status'].includes(sort)
        ? sort
        : DEFAULT_FILTERS.sort,
    dir: params.get('dir') === '-1' ? -1 : 1,
    view: view === 'map' ? 'map' : 'table',
  };
}

/** Only non-default values reach the URL, so a plain link stays clean. */
export function writeFilters(f: FilterState): URLSearchParams {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.region !== DEFAULT_FILTERS.region) p.set('region', f.region);
  if (f.brand !== DEFAULT_FILTERS.brand) p.set('brand', f.brand);
  if (f.state !== DEFAULT_FILTERS.state) p.set('state', f.state);
  if (f.district !== DEFAULT_FILTERS.district) p.set('district', f.district);
  if (f.status !== DEFAULT_FILTERS.status) p.set('status', f.status);
  if (f.sort !== DEFAULT_FILTERS.sort) p.set('sort', f.sort);
  if (f.dir !== DEFAULT_FILTERS.dir) p.set('dir', String(f.dir));
  if (f.view !== DEFAULT_FILTERS.view) p.set('view', f.view);
  return p;
}

export function applyFilters(
  facilities: Facility[],
  units: Record<string, number | null>,
  f: FilterState,
): Facility[] {
  const term = f.q.trim().toLowerCase();

  const filtered = facilities.filter((x) => {
    if (f.region !== 'All' && x.region !== f.region) return false;
    if (f.brand !== 'All' && x.brand !== f.brand) return false;
    if (f.state !== 'All' && x.state !== f.state) return false;
    // 'None' is normalized to null upstream, so an unassigned district is null
    // here. It gets its own option rather than being lumped into "All".
    if (f.district !== 'All' && (x.district ?? '—') !== f.district) return false;
    if (f.status === 'Active' && !x.active) return false;
    if (f.status === 'Inactive' && x.active) return false;
    // The operational filters are scoped to managed sites, matching the tiles
    // they are driven from. An unmanaged site's vacancy is not our problem.
    if (f.status === 'Unmanned' && (!x.active || x.manned !== false)) return false;
    // `staffVacancy` is computed once by the API — the same field the
    // "Manager vacancy" tile counts — so the tile and this list cannot drift.
    if (f.status === 'Vacancy' && (!x.active || !x.staffVacancy)) return false;
    if (!term) return true;
    return [
      x.name, x.code, x.city, x.state, x.brand, x.district, x.yardiCode, x.entity, x.portfolio,
      // Everyone on the on-site team, not just the PM — a search for a senior
      // property manager should find every site they cover.
      ...teamNames(x),
    ].some((v) => (v || '').toLowerCase().includes(term));
  });

  const value = (x: Facility): string | number => {
    if (f.sort === 'units') return units[x.id] ?? -1;
    if (f.sort === 'status') return x.active ? 1 : 0;
    return (x[f.sort] ?? '').toString().toLowerCase();
  };

  return [...filtered].sort((a, b) => {
    const A = value(a);
    const B = value(b);
    if (A < B) return -1 * f.dir;
    if (A > B) return 1 * f.dir;
    // Ties break on the unique id so the order is stable across renders and
    // the stepper never reshuffles under the user.
    return a.id.localeCompare(b.id);
  });
}
